// Configuration Metadata System
// This file defines the complete metadata for all configuration values
// including types, validation rules, UI hints, and help text

// Value type definitions
export const ConfigTypes = {
  NUMBER: 'number',
  INTEGER: 'integer',
  FLOAT: 'float',
  BOOLEAN: 'boolean',
  STRING: 'string',
  COLOR: 'color',
  ENUM: 'enum',
  PERCENTAGE: 'percentage',
  MILLISECONDS: 'milliseconds',
  PIXELS: 'pixels',
  DEGREES: 'degrees',
  RADIANS: 'radians',
  MULTIPLIER: 'multiplier'
};

// UI control types
export const UIControls = {
  SLIDER: 'slider',
  NUMBER_INPUT: 'number_input',
  CHECKBOX: 'checkbox',
  COLOR_PICKER: 'color_picker',
  DROPDOWN: 'dropdown',
  RANGE_SLIDER: 'range_slider',
  STEPPER: 'stepper'
};

// Configuration metadata structure
export const CONFIG_METADATA = {
  GAME_CONFIG: {
    category: 'Core Game',
    description: 'Fundamental game mechanics and rules',
    values: {
      BASE_SPEED: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 3.0,
        step: 0.05,
        default: 0.75,
        unit: 'px/frame',
        help: 'Base movement speed for soldiers',
        tags: ['movement', 'balance']
      },
      BASE_SIZE: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 2,
        max: 10,
        step: 1,
        default: 4,
        unit: 'pixels',
        help: 'Collision radius for soldiers',
        tags: ['collision', 'visual']
      },
      BASE_HEALTH: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 10,
        max: 500,
        step: 10,
        default: 100,
        unit: 'hp',
        help: 'Starting health for level 1 soldiers',
        tags: ['combat', 'balance']
      },
      BASE_WEAPON_RANGE: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.SLIDER,
        min: 20,
        max: 200,
        step: 5,
        default: 80,
        unit: 'pixels',
        help: 'Attack range for soldiers',
        tags: ['combat', 'balance']
      },
      BASE_DAMAGE: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.NUMBER_INPUT,
        min: 0.5,
        max: 10,
        step: 0.5,
        default: 2,
        unit: 'damage',
        help: 'Damage per hit for level 1 soldiers',
        tags: ['combat', 'balance']
      },
      FLEE_HEALTH_THRESHOLD: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0,
        max: 1,
        step: 0.05,
        default: 0.3,
        format: 'percentage',
        help: 'Health percentage that triggers fleeing behavior',
        tags: ['ai', 'combat']
      },
      LEVEL_UP_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.0,
        max: 2.0,
        step: 0.05,
        default: 1.2,
        format: 'multiplier',
        help: 'Stat increase multiplier per level (1.2 = 20% increase)',
        tags: ['progression', 'balance']
      },
      MAX_LEVEL: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 5,
        max: 20,
        step: 1,
        default: 15,
        help: 'Maximum soldier level',
        tags: ['progression']
      },
      INITIAL_UNITS_PER_TEAM: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 1,
        max: 100,
        step: 5,
        default: 50,
        help: 'Starting soldiers per team',
        tags: ['spawn', 'balance']
      },
      SPAWN_MARGIN: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 0,
        max: 200,
        step: 10,
        default: 50,
        unit: 'pixels',
        help: 'Minimum distance from screen edges for spawning',
        tags: ['spawn', 'visual']
      },
      AMMO_CAPACITY: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.SLIDER,
        min: 10,
        max: 500,
        step: 10,
        default: 100,
        unit: 'shots',
        help: 'Maximum ammunition before needing resupply',
        tags: ['combat', 'resource']
      },
      RESOURCE_CARRY_SPEED_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 1.0,
        step: 0.05,
        default: 0.6,
        format: 'percentage',
        help: 'Speed reduction when carrying resources (0.6 = 60% speed)',
        tags: ['movement', 'resource']
      },
      MAX_TOTAL_UNITS: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 50,
        max: 1000,
        step: 50,
        default: 300,
        help: 'Global unit limit across all teams',
        tags: ['performance', 'balance']
      },
      MAX_UNITS_PER_TEAM: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 10,
        max: 200,
        step: 10,
        default: 10,
        help: 'Maximum units per individual team',
        tags: ['balance']
      }
    }
  },

  AI_CONFIG: {
    category: 'AI Behavior',
    description: 'Artificial intelligence and decision-making parameters',
    values: {
      FIRE_RATE: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.SLIDER,
        min: 100,
        max: 2000,
        step: 50,
        default: 500,
        unit: 'ms',
        help: 'Base time between shots',
        tags: ['combat', 'timing']
      },
      MIN_FIRE_RATE: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.SLIDER,
        min: 50,
        max: 1000,
        step: 50,
        default: 200,
        unit: 'ms',
        help: 'Minimum fire rate for veteran soldiers',
        tags: ['combat', 'progression']
      },
      PERCEPTION_RANGE: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 50,
        max: 300,
        step: 10,
        default: 80,
        unit: 'pixels',
        help: 'Distance soldiers can detect enemies',
        tags: ['ai', 'vision']
      },
      DECISION_INTERVAL: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.NUMBER_INPUT,
        min: 50,
        max: 500,
        step: 10,
        default: 100,
        unit: 'ms',
        help: 'Time between AI decision updates',
        tags: ['ai', 'performance']
      },
      MAX_COURAGE: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 50,
        max: 200,
        step: 10,
        default: 100,
        help: 'Maximum courage value',
        tags: ['ai', 'combat']
      },
      COURAGE_RECOVERY_RATE: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 2.0,
        step: 0.1,
        default: 0.5,
        unit: '/frame',
        help: 'Courage recovery per frame',
        tags: ['ai', 'combat']
      },
      FLEE_DURATION: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.SLIDER,
        min: 30,
        max: 300,
        step: 10,
        default: 60,
        unit: 'frames',
        help: 'Minimum frames to flee when scared',
        tags: ['ai', 'behavior']
      },
      PREDICTION_ACCURACY: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0,
        max: 1,
        step: 0.05,
        default: 0.5,
        format: 'percentage',
        help: 'Base accuracy for aim prediction',
        tags: ['ai', 'combat']
      },
      SEPARATION_RADIUS: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 5,
        max: 50,
        step: 5,
        default: 18,
        unit: 'pixels',
        help: 'Distance to maintain from teammates',
        tags: ['ai', 'movement']
      },
      SEPARATION_FORCE: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 1.0,
        step: 0.05,
        default: 0.3,
        help: 'Strength of separation behavior',
        tags: ['ai', 'movement']
      },
      CORPSE_COLLECTION_RADIUS: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 10,
        max: 50,
        step: 5,
        default: 25,
        unit: 'pixels',
        help: 'Distance to collect corpses',
        tags: ['ai', 'behavior']
      },
      CORPSE_SEEK_PRIORITY: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0,
        max: 1,
        step: 0.1,
        default: 1.0,
        format: 'percentage',
        help: 'How much AI prioritizes corpses over combat',
        tags: ['ai', 'behavior']
      },
      BASE_AVOIDANCE_RADIUS: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 50,
        max: 200,
        step: 10,
        default: 80,
        unit: 'pixels',
        help: 'Distance to avoid enemy bases',
        tags: ['ai', 'movement']
      },
      BASE_AVOIDANCE_FORCE: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 1.0,
        step: 0.05,
        default: 0.4,
        help: 'Strength of base avoidance behavior',
        tags: ['ai', 'movement']
      },
      BASE_TARGETING_MIN_LEVEL: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 1,
        max: 15,
        step: 1,
        default: 10,
        help: 'Minimum level to opportunistically attack bases',
        tags: ['ai', 'combat', 'progression']
      },
      BASE_HUNTING_MIN_LEVEL: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 1,
        max: 15,
        step: 1,
        default: 12,
        help: 'Minimum level to actively hunt bases',
        tags: ['ai', 'combat', 'progression']
      }
    }
  },

  DEBUG_CONFIG: {
    category: 'Debug Settings',
    description: 'Debug visualization toggles',
    values: {
      targetingLines: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: true,
        help: 'Show red lines between soldiers and targets',
        tags: ['debug', 'visual']
      },
      predictedPositions: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: true,
        help: 'Show white X marks for aim prediction',
        tags: ['debug', 'visual']
      },
      stats: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: true,
        help: 'Show performance statistics',
        tags: ['debug', 'performance']
      }
    }
  },

  DEBUG_VISUALIZATION_CONFIG: {
    category: 'Debug Visualizations',
    description: 'Advanced debug overlay options',
    values: {
      showPerceptionRadius: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: false,
        help: 'Display perception range circles',
        tags: ['debug', 'ai']
      },
      showWeaponRadius: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: false,
        help: 'Display weapon range circles',
        tags: ['debug', 'combat']
      },
      showUnitState: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: true,
        help: 'Show current AI state above units',
        tags: ['debug', 'ai']
      },
      showTargetLines: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: true,
        help: 'Draw lines to current targets',
        tags: ['debug', 'combat']
      },
      showPredictedPositions: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: true,
        help: 'Show predicted target positions',
        tags: ['debug', 'ai']
      },
      showPathfinding: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: false,
        help: 'Display pathfinding information',
        tags: ['debug', 'ai']
      },
      showCollisionBoxes: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: false,
        help: 'Show collision boundaries',
        tags: ['debug', 'physics']
      },
      showHealthBars: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: true,
        help: 'Display health bars above units',
        tags: ['debug', 'ui']
      },
      showAmmoCount: {
        type: ConfigTypes.BOOLEAN,
        ui: UIControls.CHECKBOX,
        default: true,
        help: 'Show remaining ammo count',
        tags: ['debug', 'ui']
      },
      colors: {
        _nested: true,
        perceptionRadius: {
          type: ConfigTypes.COLOR,
          ui: UIControls.COLOR_PICKER,
          default: 'rgba(0, 255, 0, 0.2)',
          help: 'Color for perception radius circles',
          tags: ['debug', 'color']
        },
        weaponRadius: {
          type: ConfigTypes.COLOR,
          ui: UIControls.COLOR_PICKER,
          default: 'rgba(255, 0, 0, 0.2)',
          help: 'Color for weapon range circles',
          tags: ['debug', 'color']
        },
        unitStateText: {
          type: ConfigTypes.COLOR,
          ui: UIControls.COLOR_PICKER,
          default: '#0f0',
          help: 'Color for unit state text',
          tags: ['debug', 'color']
        },
        pathfinding: {
          type: ConfigTypes.COLOR,
          ui: UIControls.COLOR_PICKER,
          default: 'rgba(255, 255, 0, 0.5)',
          help: 'Color for pathfinding visualization',
          tags: ['debug', 'color']
        },
        collisionBox: {
          type: ConfigTypes.COLOR,
          ui: UIControls.COLOR_PICKER,
          default: 'rgba(0, 255, 255, 0.3)',
          help: 'Color for collision boxes',
          tags: ['debug', 'color']
        }
      }
    }
  },

  COMBAT_CONFIG: {
    category: 'Combat',
    description: 'Weapon mechanics and combat calculations',
    values: {
      WEAPON_SPREAD_MIN: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0,
        max: 1,
        step: 0.05,
        default: 0.2,
        unit: 'radians',
        help: 'Minimum weapon spread angle',
        tags: ['combat', 'accuracy']
      },
      SKILL_TO_SPREAD_DIVISOR: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.NUMBER_INPUT,
        min: 10,
        max: 100,
        step: 5,
        default: 50,
        help: 'Higher values = less spread improvement with skill',
        tags: ['combat', 'progression']
      },
      SPREAD_MULTIPLIER: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 1,
        max: 20,
        step: 1,
        default: 10,
        help: 'Spread range multiplier',
        tags: ['combat', 'accuracy']
      },
      PROJECTILE_START_OFFSET: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.STEPPER,
        min: 0,
        max: 10,
        step: 1,
        default: 2,
        unit: 'pixels',
        help: 'Distance from soldier edge to spawn projectiles',
        tags: ['combat', 'visual']
      },
      AUTO_TARGET_RANGE_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.0,
        max: 3.0,
        step: 0.1,
        default: 2.0,
        format: 'multiplier',
        help: 'Range multiplier for auto-targeting',
        tags: ['combat', 'ai']
      },
      COMBAT_MIN_DISTANCE: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.3,
        max: 1.0,
        step: 0.05,
        default: 0.7,
        format: 'percentage',
        help: 'Minimum combat distance as percentage of weapon range',
        tags: ['combat', 'ai']
      },
      COMBAT_MAX_DISTANCE: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 1.0,
        step: 0.05,
        default: 0.9,
        format: 'percentage',
        help: 'Maximum combat distance as percentage of weapon range',
        tags: ['combat', 'ai']
      },
      COMBAT_IDEAL_DISTANCE: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 1.0,
        step: 0.05,
        default: 0.85,
        format: 'percentage',
        help: 'Preferred engagement distance before strafing begins',
        tags: ['combat', 'ai']
      },
      COMBAT_STRAFE_DISTANCE: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 1.0,
        step: 0.05,
        default: 0.8,
        format: 'percentage',
        help: 'Ideal strafing distance as percentage of weapon range',
        tags: ['combat', 'ai']
      },
      TARGET_SWITCH_RANGE: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.0,
        max: 2.0,
        step: 0.1,
        default: 1.5,
        format: 'multiplier',
        help: 'Range multiplier for checking better targets',
        tags: ['combat', 'ai']
      },
      COURAGE_DAMAGE_REDUCTION: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.SLIDER,
        min: 1,
        max: 50,
        step: 5,
        default: 10,
        help: 'Courage lost when taking damage',
        tags: ['combat', 'ai']
      },
      DAMAGE_INDICATOR_DECAY: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.01,
        max: 0.1,
        step: 0.01,
        default: 0.05,
        unit: '/frame',
        help: 'Damage indicator fade speed',
        tags: ['visual', 'combat']
      },
      SKILL_INCREASE_PER_LEVEL: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 5,
        max: 50,
        step: 5,
        default: 20,
        help: 'Skill points gained per level',
        tags: ['progression', 'combat']
      },
      MAX_SKILL_CAP: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 50,
        max: 200,
        step: 10,
        default: 100,
        help: 'Maximum skill value',
        tags: ['progression', 'combat']
      },
      FIRE_RATE_IMPROVEMENT: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 1.0,
        step: 0.05,
        default: 0.8,
        format: 'multiplier',
        help: 'Fire rate multiplier per level (lower = faster)',
        tags: ['progression', 'combat']
      },
      MAX_PREDICTION_ACCURACY: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 1.0,
        step: 0.05,
        default: 0.85,
        format: 'percentage',
        help: 'Maximum targeting prediction accuracy',
        tags: ['combat', 'ai']
      },
      AMMO_DONATION_PERCENTAGE: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 0.5,
        step: 0.05,
        default: 0.25,
        format: 'percentage',
        help: 'Percentage of ammo to donate to bases',
        tags: ['resource', 'combat']
      },
      CORPSE_AMMO_GRANTED: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 10,
        max: 100,
        step: 5,
        default: 25,
        unit: 'ammo',
        help: 'Ammo granted when looting a corpse',
        tags: ['resource', 'combat']
      }
    }
  },

  MOVEMENT_CONFIG: {
    category: 'Movement',
    description: 'Speed multipliers and movement mechanics',
    values: {
      SOLDIER_SPEED_MIN: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 1.0,
        step: 0.05,
        default: 0.8,
        format: 'multiplier',
        help: 'Minimum speed variation for soldiers',
        tags: ['movement', 'variety']
      },
      SOLDIER_SPEED_MAX: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.0,
        max: 1.5,
        step: 0.05,
        default: 1.2,
        format: 'multiplier',
        help: 'Maximum speed variation for soldiers',
        tags: ['movement', 'variety']
      },
      LEVEL_SPEED_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.0,
        max: 1.5,
        step: 0.05,
        default: 1.1,
        format: 'multiplier',
        help: 'Speed increase per level',
        tags: ['movement', 'progression']
      },
      FLEE_SPEED_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.0,
        max: 2.0,
        step: 0.1,
        default: 1.5,
        format: 'multiplier',
        help: 'Speed boost when fleeing',
        tags: ['movement', 'ai']
      },
      URGENT_SPEED_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.0,
        max: 1.5,
        step: 0.05,
        default: 1.2,
        format: 'multiplier',
        help: 'Speed for urgent movement',
        tags: ['movement', 'ai']
      },
      QUICK_SPEED_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.0,
        max: 1.3,
        step: 0.05,
        default: 1.1,
        format: 'multiplier',
        help: 'Speed for quick movement',
        tags: ['movement', 'ai']
      },
      PATROL_SPEED_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 0.3,
        max: 1.0,
        step: 0.05,
        default: 0.7,
        format: 'multiplier',
        help: 'Speed when patrolling',
        tags: ['movement', 'ai']
      },
      WANDER_SPEED_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 1.0,
        step: 0.05,
        default: 0.8,
        format: 'multiplier',
        help: 'Speed when wandering',
        tags: ['movement', 'ai']
      },
      RESOURCE_APPROACH_SPEED: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 0.5,
        step: 0.05,
        default: 0.3,
        format: 'multiplier',
        help: 'Speed when approaching distant resources',
        tags: ['movement', 'resource']
      },
      RETURN_RESOURCE_SPEED: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 0.3,
        max: 1.0,
        step: 0.05,
        default: 0.6,
        format: 'multiplier',
        help: 'Speed when returning resources to base',
        tags: ['movement', 'resource']
      },
      MIN_VELOCITY_THRESHOLD: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.NUMBER_INPUT,
        min: 0.01,
        max: 0.5,
        step: 0.01,
        default: 0.1,
        unit: 'px/frame',
        help: 'Minimum velocity before considered stopped',
        tags: ['movement', 'physics']
      },
      MAX_TURN_RATE: {
        type: ConfigTypes.RADIANS,
        ui: UIControls.SLIDER,
        min: 0.01,
        max: 0.2,
        step: 0.01,
        default: 0.05,
        unit: 'rad/frame',
        help: 'Maximum rotation speed for smooth turning',
        tags: ['movement', 'visual']
      },
      ALLY_SEPARATION_RADIUS: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 10,
        max: 50,
        step: 5,
        default: 25,
        unit: 'pixels',
        help: 'Distance to maintain from allies',
        tags: ['movement', 'ai']
      },
      ALLY_SEPARATION_FORCE: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 1.0,
        step: 0.05,
        default: 0.4,
        help: 'Strength of ally separation',
        tags: ['movement', 'ai']
      }
    }
  },

  RESOURCE_CONFIG: {
    category: 'Resources',
    description: 'Resource spawning and collection mechanics',
    values: {
      METAL_SPAWN_INTERVAL: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.NUMBER_INPUT,
        min: 1000,
        max: 30000,
        step: 1000,
        default: 5000,
        unit: 'ms',
        help: 'Time between metal spawns',
        tags: ['resource', 'spawn']
      },
      NEBULA_SPAWN_INTERVAL: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.NUMBER_INPUT,
        min: 10000,
        max: 60000,
        step: 5000,
        default: 35000,
        unit: 'ms',
        help: 'Time between nebula spawns',
        tags: ['resource', 'spawn']
      },
      CRYSTAL_SPAWN_INTERVAL: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.NUMBER_INPUT,
        min: 60000,
        max: 300000,
        step: 10000,
        default: 150000,
        unit: 'ms',
        help: 'Time between crystal spawns',
        tags: ['resource', 'spawn']
      },
      METAL_TO_AMMO_RATIO: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.SLIDER,
        min: 10,
        max: 100,
        step: 10,
        default: 50,
        unit: 'ammo/metal',
        help: 'Ammo generated per metal resource',
        tags: ['resource', 'conversion']
      },
      ORGANICS_TO_SPAWN_THRESHOLD: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 5,
        max: 20,
        step: 1,
        default: 10,
        unit: 'organics',
        help: 'Organics needed to spawn a soldier',
        tags: ['resource', 'spawn']
      },
      CRYSTAL_UNIT_SPAWNS: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 1,
        max: 10,
        step: 1,
        default: 3,
        unit: 'units',
        help: 'Number of units spawned by crystal',
        tags: ['resource', 'spawn']
      },
      NEBULA_RADIUS: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 20,
        max: 50,
        step: 5,
        default: 35,
        unit: 'pixels',
        help: 'Collection radius for nebulas',
        tags: ['resource', 'collection']
      },
      MAX_GATHERERS_PER_NEBULA: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 1,
        max: 10,
        step: 1,
        default: 5,
        unit: 'gatherers',
        help: 'Maximum simultaneous gatherers per nebula',
        tags: ['resource', 'collection']
      },
      ORGANICS_PER_GATHERER_PER_SECOND: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 3.0,
        step: 0.5,
        default: 1.0,
        unit: 'org/sec',
        help: 'Collection rate per gatherer',
        tags: ['resource', 'collection']
      },
      METAL_VALUE: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 1,
        max: 10,
        step: 1,
        default: 1,
        unit: 'metal',
        help: 'Metal units collected per resource',
        tags: ['resource', 'value']
      },
      MAX_RESOURCES_ON_MAP: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 10,
        max: 100,
        step: 10,
        default: 50,
        unit: 'resources',
        help: 'Maximum resources allowed on map',
        tags: ['resource', 'performance']
      }
    }
  },

  BASE_CONFIG: {
    category: 'Bases',
    description: 'Base properties and behavior',
    values: {
      HEALTH: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 100,
        max: 1000,
        step: 50,
        default: 300,
        unit: 'hp',
        help: 'Base starting health',
        tags: ['base', 'combat']
      },
      RANGE: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 100,
        max: 300,
        step: 10,
        default: 180,
        unit: 'pixels',
        help: 'Base defense turret range',
        tags: ['base', 'combat']
      },
      DAMAGE: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.NUMBER_INPUT,
        min: 1,
        max: 10,
        step: 0.5,
        default: 3,
        unit: 'damage',
        help: 'Base turret damage',
        tags: ['base', 'combat']
      },
      SPAWN_INTERVAL: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.NUMBER_INPUT,
        min: 10000,
        max: 60000,
        step: 5000,
        default: 30000,
        unit: 'ms',
        help: 'Time between soldier spawns',
        tags: ['base', 'spawn']
      },
      REPAIR_THRESHOLD: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.STEPPER,
        min: 10,
        max: 15,
        step: 1,
        default: 15,
        help: 'Maximum level required to repair bases',
        tags: ['base', 'progression']
      },
      PRIORITY_DURATION: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.NUMBER_INPUT,
        min: 5000,
        max: 30000,
        step: 5000,
        default: 10000,
        unit: 'ms',
        help: 'Duration of priority states',
        tags: ['base', 'ai']
      },
      AMMO_LOW_THRESHOLD: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 0.5,
        step: 0.05,
        default: 0.3,
        format: 'percentage',
        help: 'Ammo percentage to request metal',
        tags: ['base', 'resource']
      },
      AMMO_CRITICAL_THRESHOLD: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.05,
        max: 0.2,
        step: 0.05,
        default: 0.1,
        format: 'percentage',
        help: 'Ammo percentage for critical state',
        tags: ['base', 'resource']
      },
      HEAL_RATE: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 2.0,
        step: 0.1,
        default: 0.5,
        unit: 'hp/frame',
        help: 'Health restored per frame when healing',
        tags: ['base', 'healing']
      },
      RESUPPLY_RATE: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 5.0,
        step: 0.5,
        default: 2.0,
        unit: 'ammo/frame',
        help: 'Ammo restored per frame when resupplying',
        tags: ['base', 'resource']
      }
    }
  },

  PROJECTILE_CONFIG: {
    category: 'Projectiles',
    description: 'Projectile physics and visual effects',
    values: {
      BASE_SPEED: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 1,
        max: 5,
        step: 0.5,
        default: 2,
        unit: 'px/frame',
        help: 'Projectile travel speed',
        tags: ['projectile', 'physics']
      },
      TRAIL_LENGTH: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.SLIDER,
        min: 5,
        max: 30,
        step: 5,
        default: 12,
        unit: 'frames',
        help: 'Length of projectile trail effect',
        tags: ['projectile', 'visual']
      },
      MAX_LIFETIME: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.NUMBER_INPUT,
        min: 50,
        max: 500,
        step: 50,
        default: 200,
        unit: 'frames',
        help: 'Maximum frames before expiration',
        tags: ['projectile', 'performance']
      },
      MISSILE_SIZE_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.5,
        max: 5.0,
        step: 0.5,
        default: 2.5,
        format: 'multiplier',
        help: 'Size multiplier for base missiles',
        tags: ['projectile', 'visual']
      },
      MISSILE_TRAIL_LENGTH: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.SLIDER,
        min: 10,
        max: 50,
        step: 5,
        default: 20,
        unit: 'frames',
        help: 'Trail length for missiles',
        tags: ['projectile', 'visual']
      },
      MISSILE_EXPLOSION_RADIUS: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 20,
        max: 60,
        step: 5,
        default: 30,
        unit: 'pixels',
        help: 'Explosion effect radius',
        tags: ['projectile', 'visual']
      },
      MISSILE_EXPLOSION_DURATION: {
        type: ConfigTypes.INTEGER,
        ui: UIControls.SLIDER,
        min: 10,
        max: 30,
        step: 5,
        default: 15,
        unit: 'frames',
        help: 'Frames for explosion animation',
        tags: ['projectile', 'visual']
      }
    }
  },

  THRESHOLD_CONFIG: {
    category: 'Thresholds',
    description: 'Game balance thresholds and limits',
    values: {
      FLEE_HEALTH_THRESHOLD: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 0.5,
        step: 0.05,
        default: 0.3,
        format: 'percentage',
        help: 'Health percentage to trigger fleeing',
        tags: ['threshold', 'ai']
      },
      LOW_HEALTH_THRESHOLD: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.2,
        max: 0.6,
        step: 0.05,
        default: 0.5,
        format: 'percentage',
        help: 'Health percentage for low health state',
        tags: ['threshold', 'ui']
      },
      LOW_AMMO_THRESHOLD: {
        type: ConfigTypes.PERCENTAGE,
        ui: UIControls.SLIDER,
        min: 0.1,
        max: 0.5,
        step: 0.05,
        default: 0.2,
        format: 'percentage',
        help: 'Ammo percentage for low ammo state',
        tags: ['threshold', 'resource']
      },
      RESOURCE_COLLECTION_RANGE: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 10,
        max: 50,
        step: 5,
        default: 20,
        unit: 'pixels',
        help: 'Distance to collect resources',
        tags: ['threshold', 'resource']
      },
      STUCK_VELOCITY_THRESHOLD: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.NUMBER_INPUT,
        min: 0.01,
        max: 0.5,
        step: 0.01,
        default: 0.1,
        unit: 'px/frame',
        help: 'Velocity to consider unit stuck',
        tags: ['threshold', 'movement']
      },
      STUCK_TIME_THRESHOLD: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.NUMBER_INPUT,
        min: 1000,
        max: 10000,
        step: 500,
        default: 3000,
        unit: 'ms',
        help: 'Time before unit is considered stuck',
        tags: ['threshold', 'movement']
      },
      OFF_SCREEN_PREDICTION_LIMIT: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.NUMBER_INPUT,
        min: 50,
        max: 500,
        step: 50,
        default: 200,
        unit: 'pixels',
        help: 'Max distance for off-screen predictions',
        tags: ['threshold', 'ai']
      }
    }
  },

  BERSERKER_CONFIG: {
    category: 'Berserkers',
    description: 'Max-level unit properties',
    values: {
      DAMAGE_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.5,
        max: 3.0,
        step: 0.1,
        default: 2.0,
        format: 'multiplier',
        help: 'Damage multiplier for max-level units',
        tags: ['berserker', 'combat']
      },
      SPEED_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.2,
        max: 2.0,
        step: 0.1,
        default: 1.5,
        format: 'multiplier',
        help: 'Speed multiplier for max-level units',
        tags: ['berserker', 'movement']
      },
      HEALTH_MULTIPLIER: {
        type: ConfigTypes.MULTIPLIER,
        ui: UIControls.SLIDER,
        min: 1.5,
        max: 3.0,
        step: 0.1,
        default: 2.0,
        format: 'multiplier',
        help: 'Health multiplier for max-level units',
        tags: ['berserker', 'combat']
      },
      GLOW_INTENSITY: {
        type: ConfigTypes.FLOAT,
        ui: UIControls.SLIDER,
        min: 0.5,
        max: 2.0,
        step: 0.1,
        default: 1.0,
        help: 'Visual glow effect intensity',
        tags: ['berserker', 'visual']
      },
      REPAIR_RANGE: {
        type: ConfigTypes.PIXELS,
        ui: UIControls.SLIDER,
        min: 20,
        max: 100,
        step: 10,
        default: 50,
        unit: 'pixels',
        help: 'Range to repair destroyed bases',
        tags: ['berserker', 'ability']
      },
      REPAIR_TIME: {
        type: ConfigTypes.MILLISECONDS,
        ui: UIControls.NUMBER_INPUT,
        min: 1000,
        max: 10000,
        step: 500,
        default: 3000,
        unit: 'ms',
        help: 'Time to repair a base',
        tags: ['berserker', 'ability']
      }
    }
  }
};

// Utility functions for working with metadata

/**
 * Get metadata for a specific config value
 * @param {string} configKey - The config section (e.g., 'GAME_CONFIG')
 * @param {string} valuePath - The value path (e.g., 'BASE_SPEED' or 'colors.perceptionRadius')
 * @returns {Object|null} The metadata for the value
 */
export function getConfigMetadata(configKey, valuePath) {
  const configMeta = CONFIG_METADATA[configKey];
  if (!configMeta) return null;
  
  const pathParts = valuePath.split('.');
  let meta = configMeta.values;
  
  for (const part of pathParts) {
    if (meta && meta[part]) {
      meta = meta[part];
    } else {
      return null;
    }
  }
  
  return meta;
}

/**
 * Validate a config value against its metadata
 * @param {string} configKey - The config section
 * @param {string} valuePath - The value path
 * @param {*} value - The value to validate
 * @returns {Object} Validation result { valid: boolean, error?: string }
 */
export function validateConfigValue(configKey, valuePath, value) {
  const meta = getConfigMetadata(configKey, valuePath);
  if (!meta) {
    return { valid: false, error: 'No metadata found for this config value' };
  }
  
  // Type validation
  switch (meta.type) {
    case ConfigTypes.BOOLEAN:
      if (typeof value !== 'boolean') {
        return { valid: false, error: 'Value must be a boolean' };
      }
      break;
      
    case ConfigTypes.INTEGER:
      if (!Number.isInteger(value)) {
        return { valid: false, error: 'Value must be an integer' };
      }
      break;
      
    case ConfigTypes.FLOAT:
    case ConfigTypes.NUMBER:
    case ConfigTypes.PERCENTAGE:
    case ConfigTypes.MILLISECONDS:
    case ConfigTypes.PIXELS:
    case ConfigTypes.DEGREES:
    case ConfigTypes.RADIANS:
    case ConfigTypes.MULTIPLIER:
      if (typeof value !== 'number' || isNaN(value)) {
        return { valid: false, error: 'Value must be a number' };
      }
      break;
      
    case ConfigTypes.STRING:
    case ConfigTypes.COLOR:
      if (typeof value !== 'string') {
        return { valid: false, error: 'Value must be a string' };
      }
      break;
      
    case ConfigTypes.ENUM:
      if (meta.options && !meta.options.includes(value)) {
        return { valid: false, error: `Value must be one of: ${meta.options.join(', ')}` };
      }
      break;
  }
  
  // Range validation for numeric types
  if (typeof value === 'number' && meta.type !== ConfigTypes.BOOLEAN) {
    if (meta.min !== undefined && value < meta.min) {
      return { valid: false, error: `Value must be at least ${meta.min}` };
    }
    if (meta.max !== undefined && value > meta.max) {
      return { valid: false, error: `Value must be at most ${meta.max}` };
    }
  }
  
  return { valid: true };
}

/**
 * Format a value for display based on its metadata
 * @param {*} value - The value to format
 * @param {Object} meta - The metadata for the value
 * @returns {string} The formatted value
 */
export function formatConfigValue(value, meta) {
  if (!meta) return String(value);
  
  switch (meta.format) {
    case 'percentage':
      return `${Math.round(value * 100)}%`;
    case 'multiplier':
      return `${value}x`;
    default:
      if (meta.unit) {
        return `${value} ${meta.unit}`;
      }
      return String(value);
  }
}

/**
 * Get all config values that match specific tags
 * @param {...string} tags - Tags to filter by
 * @returns {Array} Array of { configKey, valuePath, metadata } objects
 */
export function getConfigsByTags(...tags) {
  const results = [];
  
  for (const [configKey, configData] of Object.entries(CONFIG_METADATA)) {
    for (const [valuePath, meta] of Object.entries(configData.values)) {
      if (meta.tags && tags.every(tag => meta.tags.includes(tag))) {
        results.push({ configKey, valuePath, metadata: meta });
      }
    }
  }
  
  return results;
}

/**
 * Generate a config preset based on tags and multipliers
 * @param {Object} options - Preset options
 * @returns {Object} Config object ready to be applied
 */
export function generatePreset(options) {
  const preset = {};
  
  // Example: { combat: 1.5, movement: 0.8 } would increase combat values by 50% and decrease movement by 20%
  for (const [tag, multiplier] of Object.entries(options)) {
    const configs = getConfigsByTags(tag);
    
    for (const { configKey, valuePath, metadata } of configs) {
      if (!preset[configKey]) preset[configKey] = {};
      
      // Only multiply numeric values
      if (typeof metadata.default === 'number') {
        let value = metadata.default * multiplier;
        
        // Respect min/max bounds
        if (metadata.min !== undefined) value = Math.max(metadata.min, value);
        if (metadata.max !== undefined) value = Math.min(metadata.max, value);
        
        // Round integers
        if (metadata.type === ConfigTypes.INTEGER) {
          value = Math.round(value);
        }
        
        preset[configKey][valuePath] = value;
      }
    }
  }
  
  return preset;
}

// Export aliases for simpler names used in ConfigWindow
export const getMetadataForValue = getConfigMetadata;
export const formatValue = formatConfigValue;
export const validateValue = (value, metadata) => {
  // Simple validation using just metadata
  if (metadata.min !== undefined && value < metadata.min) {
    return { valid: false, message: `Value must be at least ${metadata.min}`, value: metadata.min };
  }
  if (metadata.max !== undefined && value > metadata.max) {
    return { valid: false, message: `Value must be at most ${metadata.max}`, value: metadata.max };
  }
  return { valid: true };
};